import type { TaskFilterOption } from '@/services/api/tasksService';

import {
  ADVANCED_FILTER_KEYS,
  FilterDefinition,
  TASK_FILTERS_CONFIG,
} from '../config/filterConfig';

// --- Sorting Options ---

/**
 * Sorts options based on a fixed ordering array.
 * Unknown values are placed at the end, sorted alphabetically.
 */
const sortExplicitly = (
  options: TaskFilterOption[],
  order: readonly string[] = [],
): TaskFilterOption[] => {
  if (!order.length) return options;

  // Create map once for O(1) lookups
  const rankMap = new Map(order.map((val, idx) => [val, idx]));
  const UNKNOWN_RANK = Number.MAX_SAFE_INTEGER;

  return [...options].sort((a, b) => {
    const rankA = rankMap.get(a.value) ?? UNKNOWN_RANK;
    const rankB = rankMap.get(b.value) ?? UNKNOWN_RANK;

    // If both are known, sort by rank
    if (rankA !== rankB) {
      return rankA - rankB;
    }

    // If both are unknown (or somehow have same rank), tie-break alphabetically
    return a.label.localeCompare(b.label);
  });
};

/**
 * Sorts filter options based on the configuration for the given key.
 * - 'alphabetical': Sorts by label A-Z
 * - 'preserve': Returns original order (backend order)
 * - 'explicit': Orders by explicitOrder array, unknown values appended alphabetically
 * - undefined: Returns original order
 */
export const getSortedOptions = (
  configKey: keyof typeof TASK_FILTERS_CONFIG,
  options?: TaskFilterOption[],
): TaskFilterOption[] => {
  if (!options?.length) return [];

  const config = TASK_FILTERS_CONFIG[configKey] as FilterDefinition;
  const sortStrategy = config.sortOptions ?? 'preserve';

  if (sortStrategy === 'alphabetical') {
    return [...options].sort((a, b) => a.label.localeCompare(b.label));
  }

  if (sortStrategy === 'explicit') {
    return sortExplicitly(options, config.explicitOrder);
  }

  // 'preserve' or default
  return options;
};

/** Special filter mode: 'all' = no filter, 'include' = only matching, 'exclude' = exclude matching */
export type SpecialFilterMode = 'all' | 'include' | 'exclude';

// --- Rainmaker Logic ---

export type RainmakerMode = SpecialFilterMode;

export const RAINMAKER_OPTIONS: { label: string; value: RainmakerMode }[] = [
  { label: 'All', value: 'all' },
  { label: 'Only', value: 'include' },
  { label: 'Exclude', value: 'exclude' },
];

/**
 * Determines the current Rainmaker mode based on active filters.
 * Reads the has_active_rainmaker boolean filter value.
 */
export const getRainmakerMode = (
  filters: Record<string, string | string[] | boolean | null | undefined>,
): RainmakerMode => {
  const value = filters[ADVANCED_FILTER_KEYS.RAINMAKER];

  if (value === 'true' || value === true) {
    return 'include';
  }
  if (value === 'false' || value === false) {
    return 'exclude';
  }
  return 'all';
};

/**
 * Returns the filter updates required to set the given Rainmaker mode.
 * Used when toggling the mode in UI.
 */
export const getRainmakerFilterUpdates = (
  mode: RainmakerMode,
): Record<string, string | null> => {
  if (mode === 'include') {
    return { [ADVANCED_FILTER_KEYS.RAINMAKER]: 'true' };
  }
  if (mode === 'exclude') {
    return { [ADVANCED_FILTER_KEYS.RAINMAKER]: 'false' };
  }
  // 'all' - clear the filter
  return { [ADVANCED_FILTER_KEYS.RAINMAKER]: null };
};

/**
 * Maps API filter state to column display value for Rainmaker.
 * Used when displaying current filter state in column headers.
 */
export const mapApiRainmakerToColumnValue = (
  filters: Record<string, string | string[] | boolean | null | undefined>,
): string[] | null => {
  const mode = getRainmakerMode(filters);
  if (mode === 'include') {
    return ['true'];
  }
  if (mode === 'exclude') {
    return ['false'];
  }
  return null;
};

/**
 * Translates column filter values (from DataTable) to API filter updates for Rainmaker.
 */
export const mapColumnRainmakerToApi = (
  value: string | string[] | null,
): Record<string, string | null> => {
  if (!value || (Array.isArray(value) && value.length === 0)) {
    return getRainmakerFilterUpdates('all');
  }

  const values = Array.isArray(value) ? value : [value];
  const hasInclude = values.includes('true');
  const hasExclude = values.includes('false');

  // If both or neither are selected, default to 'all'
  if (hasInclude && !hasExclude) {
    return getRainmakerFilterUpdates('include');
  }
  if (hasExclude && !hasInclude) {
    return getRainmakerFilterUpdates('exclude');
  }

  return getRainmakerFilterUpdates('all');
};
